import { BrowserBaseLogger } from './browser-base-logger';
import { LogCategory, LogLevel } from './types';
/**
 * Browser-specific logger that extends BrowserBaseLogger with browser-specific functionality
 */
export class BrowserLogger extends BrowserBaseLogger {
    /**
     * Creates a new BrowserLogger instance
     * @param options Logger configuration options
     */
    constructor(options) {
        super(options);
        this.performanceMarks = new Map();
    }
    /**
     * Tracks user interactions (clicks, form submissions, etc.)
     * @param action The action performed
     * @param element The element that was interacted with
     * @param context Additional context information
     */
    trackUserInteraction(action, element, context) {
        this.info(`User interaction: ${action}`, {
            category: LogCategory.USER,
            action,
            element,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            ...context,
        });
    }
    /**
     * Tracks page views
     * @param path The page path
     * @param title The page title
     * @param context Additional context information
     */
    trackPageView(path, title, context) {
        this.info(`Page view: ${path}`, {
            category: LogCategory.USER,
            action: 'page_view',
            path,
            title: title || document.title,
            url: window.location.href,
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
            ...context,
        });
    }
    /**
     * Tracks API requests
     * @param method HTTP method
     * @param url Request URL
     * @param status Response status
     * @param duration Request duration in milliseconds
     * @param context Additional context information
     */
    trackApiRequest(method, url, options) {
        const { status, duration, context } = options;
        const level = status >= 400 ? LogLevel.ERROR : LogLevel.INFO;
        const message = `API ${method} ${url} - ${status} (${duration}ms)`;
        this.logToTransports(level, message, {
            category: LogCategory.API,
            action: 'api_request',
            method,
            url,
            status,
            duration,
            timestamp: new Date().toISOString(),
            ...context,
        });
    }
    /**
     * Tracks performance measurements
     * @param name The performance measurement name
     * @param duration Duration in milliseconds
     * @param context Additional context information
     */
    trackPerformance(name, duration, context) {
        this.info(`Performance: ${name} - ${duration}ms`, {
            category: LogCategory.PERF,
            action: 'performance_measurement',
            name,
            duration,
            timestamp: new Date().toISOString(),
            ...context,
        });
    }
    /**
     * Starts a performance measurement
     * @param name The measurement name
     */
    startPerformanceMeasurement(name) {
        const startTime = performance.now();
        this.performanceMarks.set(name, startTime);
        if (performance.mark) {
            performance.mark(`${name}-start`);
        }
    }
    /**
     * Ends a performance measurement and logs the result
     * @param name The measurement name
     * @param context Additional context information
     * @returns The performance measurement result
     */
    endPerformanceMeasurement(name, context) {
        const startTime = this.performanceMarks.get(name);
        if (!startTime) {
            this.warn(`Performance measurement '${name}' was not started`);
            return null;
        }
        const endTime = performance.now();
        const duration = endTime - startTime;
        // Clean up
        this.performanceMarks.delete(name);
        if (performance.mark && performance.measure) {
            performance.mark(`${name}-end`);
            performance.measure(name, `${name}-start`, `${name}-end`);
        }
        const measurement = {
            name,
            duration,
            startTime,
            endTime,
        };
        this.trackPerformance(name, duration, context);
        return measurement;
    }
    /**
     * Logs browser-specific information
     * @param context Additional context information
     */
    logBrowserInfo(context) {
        this.info('Browser information', {
            category: LogCategory.SYSTEM,
            browser: {
                userAgent: navigator.userAgent,
                language: navigator.language,
                platform: navigator.platform,
                cookieEnabled: navigator.cookieEnabled,
                onLine: navigator.onLine,
            },
            screen: {
                width: screen.width,
                height: screen.height,
                availWidth: screen.availWidth,
                availHeight: screen.availHeight,
                colorDepth: screen.colorDepth,
            },
            window: {
                innerWidth: window.innerWidth,
                innerHeight: window.innerHeight,
                outerWidth: window.outerWidth,
                outerHeight: window.outerHeight,
            },
            location: {
                href: window.location.href,
                protocol: window.location.protocol,
                host: window.location.host,
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
            },
            ...context,
        });
    }
    /**
     * Logs an unhandled error
     * @param error The error object
     * @param context Additional context information
     */
    logUnhandledError(error, context) {
        this.logError(error, 'Unhandled error', {
            category: LogCategory.ERROR,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            ...context,
        });
    }
    /**
     * Logs a promise rejection
     * @param reason The rejection reason
     * @param context Additional context information
     */
    logUnhandledRejection(reason, context) {
        const message = reason instanceof Error ? reason.message : String(reason);
        this.error(`Unhandled promise rejection: ${message}`, {
            category: LogCategory.ERROR,
            reason: reason instanceof Error
                ? {
                    name: reason.name,
                    message: reason.message,
                    stack: reason.stack,
                }
                : reason,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            ...context,
        });
    }
}
//# sourceMappingURL=browser-logger.js.map